import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Upload, Check, ChevronLeft, ChevronRight, Home, TrendingUp, GraduationCap, Target, DollarSign, Briefcase } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const COLORS = {
  beige: '#F5F3ED',
  navy: '#1a1a3e',
  orange: '#FF8C42',
  white: '#ffffff'
};

const OnboardingNew = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  
  // Pre-populate from signup
  const [formData, setFormData] = useState(() => {
    const signupData = localStorage.getItem('signupData');
    const parsed = signupData ? JSON.parse(signupData) : {};
    return {
      // Personal Info (pre-filled from signup)
      firstName: parsed.firstName || '',
      lastName: parsed.lastName || '',
      email: parsed.email || '',
      phone: '',
      dateOfBirth: '',
      
      // ID Verification
      idFrontImage: null,
      idBackImage: null,
      selfieImage: null,
      
      // Address
      streetAddress: '',
      suburb: '',
      state: '',
      postcode: '',
      
      // Investment Profile
      incomeStability: '',
      portfolioDropScenario: '',
      assetExperience: [],
      investmentObjective: '',
      timeHorizon: '',
      
      // Goal Setting
      goalCategory: '',
      goalName: '',
      goalAmount: '',
      goalTimeHorizon: '',
      goalImage: null,
      
      // Banking
      bankName: '',
      accountNumber: '',
      bsb: '',
      initialDeposit: ''
    };
  });

  const totalSteps = 7;

  const stepTitles = [
    'Personal Information',
    'Identity Verification',
    'Address',
    'Investment Profile',
    'Set Your Goal',
    'Link Your Bank',
    'Make First Deposit'
  ];

  useEffect(() => {
    // Clear signup data after loading
    if (currentStep === 1) {
      localStorage.removeItem('signupData');
    }
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      // Handle multi-select for asset experience
      setFormData(prev => {
        const current = prev.assetExperience || [];
        if (checked) {
          return { ...prev, assetExperience: [...current, value] };
        } else {
          return { ...prev, assetExperience: current.filter(item => item !== value) };
        }
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileUpload = (field, file) => {
    if (file) {
      setFormData(prev => ({ ...prev, [field]: file }));
      toast({
        title: 'File uploaded',
        description: `${file.name} uploaded successfully`
      });
    }
  };

  const handleGoalCategorySelect = (category) => {
    setFormData(prev => ({ 
      ...prev, 
      goalCategory: category,
      goalName: category === 'custom' ? '' : category
    }));
  };

  const validateStep = () => {
    switch(currentStep) {
      case 1: // Personal Info
        return formData.phone && formData.dateOfBirth;
      case 2: // ID Verification
        return formData.idFrontImage && formData.idBackImage && formData.selfieImage;
      case 3: // Address
        return formData.streetAddress && formData.suburb && formData.state && formData.postcode;
      case 4: // Investment Profile
        return formData.incomeStability && formData.portfolioDropScenario && 
               formData.assetExperience.length > 0 && formData.timeHorizon;
      case 5: // Goal Setting
        return formData.goalName && formData.goalAmount && formData.goalTimeHorizon;
      case 6: // Bank Linking
        return formData.bankName && formData.accountNumber && formData.bsb;
      case 7: // First Deposit
        return formData.initialDeposit && parseFloat(formData.initialDeposit) >= 1000;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      } else {
        handleComplete();
      }
    } else {
      toast({
        title: 'Incomplete information',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem('userEmail', formData.email);
    localStorage.setItem('userName', `${formData.firstName} ${formData.lastName}`);
    localStorage.setItem('onboardingComplete', 'true');
    
    toast({
      title: 'Welcome to Trove!',
      description: 'Your account is all set up'
    });
    
    navigate('/dashboard');
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <p className="text-gray-600 mb-6">
        We just need a couple more details to get you started.
      </p>
      
      <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-500">First Name</label>
          <input
            type="text"
            value={formData.firstName}
            disabled
            className="w-full px-4 py-3 border border-gray-200 rounded-md bg-gray-100 text-gray-600"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-500">Last Name</label>
          <input
            type="text"
            value={formData.lastName}
            disabled
            className="w-full px-4 py-3 border border-gray-200 rounded-md bg-gray-100 text-gray-600"
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-2 text-gray-500">Email</label>
          <input
            type="email"
            value={formData.email}
            disabled
            className="w-full px-4 py-3 border border-gray-200 rounded-md bg-gray-100 text-gray-600"
          />
        </div>
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          Mobile Number
        </label>
        <input
          name="phone"
          type="tel"
          required
          value={formData.phone}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
          placeholder="04XX XXX XXX"
        />
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          Date of Birth
        </label>
        <input
          name="dateOfBirth"
          type="date"
          required
          value={formData.dateOfBirth}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <p className="text-gray-600 mb-6">
        We need to verify your identity. Please upload your driver's licence and a selfie.
      </p>

      {['idFrontImage', 'idBackImage', 'selfieImage'].map((field, idx) => {
        const labels = ["Driver's Licence (Front)", "Driver's Licence (Back)", "Selfie"];
        return (
          <div key={field}>
            <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
              {labels[idx]}
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#FF8C42] transition-colors cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(field, e.target.files[0])}
                className="hidden"
                id={field}
              />
              <label htmlFor={field} className="cursor-pointer">
                {formData[field] ? (
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <Check size={20} />
                    <span>{formData[field].name}</span>
                  </div>
                ) : (
                  <div>
                    <Upload className="mx-auto mb-2" size={32} style={{ color: COLORS.orange }} />
                    <p className="text-sm text-gray-600">Click to upload {labels[idx].toLowerCase()}</p>
                  </div>
                )}
              </label>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <p className="text-gray-600 mb-6">
        Where should we send important mail?
      </p>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          Street Address
        </label>
        <input
          name="streetAddress"
          type="text"
          required
          value={formData.streetAddress}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
          placeholder="123 Collins Street"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
            Suburb
          </label>
          <input
            name="suburb"
            type="text"
            required
            value={formData.suburb}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
            placeholder="Melbourne"
          />
        </div>
        <div>
          <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
            State
          </label>
          <select
            name="state"
            required
            value={formData.state}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
          >
            <option value="">Select</option>
            <option value="NSW">NSW</option>
            <option value="VIC">VIC</option>
            <option value="QLD">QLD</option>
            <option value="SA">SA</option>
            <option value="WA">WA</option>
            <option value="TAS">TAS</option>
            <option value="NT">NT</option>
            <option value="ACT">ACT</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          Postcode
        </label>
        <input
          name="postcode"
          type="text"
          required
          value={formData.postcode}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
          placeholder="3000"
          maxLength="4"
        />
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-8">
      <p className="text-gray-600 mb-6">
        To kick things off, let's get your take on risk and financial ambitions.
      </p>

      <div>
        <label className="block text-base font-medium mb-4" style={{ color: COLORS.navy }}>
          How would you describe your income stability over the next 5 years?
        </label>
        <div className="space-y-3">
          {['Highly Stable', 'Stable', 'Moderately Stable', 'Unstable', 'Highly Unstable'].map((option) => (
            <label key={option} className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-[#FF8C42] transition-colors">
              <input
                type="radio"
                name="incomeStability"
                value={option}
                checked={formData.incomeStability === option}
                onChange={handleInputChange}
                className="mr-3"
                style={{ accentColor: COLORS.orange }}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-base font-medium mb-4" style={{ color: COLORS.navy }}>
          What would you do if your portfolio dropped 30% in three months?
        </label>
        <div className="space-y-3">
          {[
            { value: 'buy', label: "Buy more - it's a great opportunity" },
            { value: 'hold', label: 'Hold - stay the course' },
            { value: 'reduce', label: 'Reduce my position' },
            { value: 'sell', label: 'Sell everything' }
          ].map((option) => (
            <label key={option.value} className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-[#FF8C42] transition-colors">
              <input
                type="radio"
                name="portfolioDropScenario"
                value={option.value}
                checked={formData.portfolioDropScenario === option.value}
                onChange={handleInputChange}
                className="mr-3"
                style={{ accentColor: COLORS.orange }}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-base font-medium mb-4" style={{ color: COLORS.navy }}>
          Which assets have you invested in before? (Select all that apply)
        </label>
        <div className="grid grid-cols-2 gap-3">
          {['Shares', 'Property', 'Fixed Income', 'Crypto', 'None'].map((asset) => (
            <label key={asset} className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-[#FF8C42] transition-colors">
              <input
                type="checkbox"
                value={asset}
                checked={formData.assetExperience.includes(asset)}
                onChange={handleInputChange}
                className="mr-3"
                style={{ accentColor: COLORS.orange }}
              />
              <span>{asset}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-base font-medium mb-4" style={{ color: COLORS.navy }}>
          What's your investment time horizon?
        </label>
        <select
          name="timeHorizon"
          required
          value={formData.timeHorizon}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
        >
          <option value="">Select your time horizon</option>
          <option value="1">1 year</option>
          <option value="2">2 years</option>
          <option value="3">3 years</option>
          <option value="5">5 years</option>
          <option value="7">7 years</option>
          <option value="10">10 years</option>
          <option value="15+">15+ years</option>
        </select>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <p className="text-gray-600 mb-6">
        Let's set up your first wealth goal.
      </p>

      <div>
        <label className="block text-base font-medium mb-4" style={{ color: COLORS.navy }}>
          What are you saving for?
        </label>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {[
            { value: 'House Deposit', icon: Home },
            { value: 'Early Retirement', icon: TrendingUp },
            { value: "Children's Education", icon: GraduationCap },
            { value: 'Wealth Building', icon: DollarSign },
            { value: 'Holiday Home', icon: Briefcase }
          ].map((goal) => {
            const IconComponent = goal.icon;
            return (
              <button
                key={goal.value}
                type="button"
                onClick={() => handleGoalCategorySelect(goal.value)}
                className="p-4 border-2 rounded-lg text-left transition-all"
                style={{
                  borderColor: formData.goalCategory === goal.value ? COLORS.orange : '#e5e7eb',
                  backgroundColor: formData.goalCategory === goal.value ? `${COLORS.orange}10` : 'white'
                }}
              >
                <div className="mb-2">
                  <IconComponent 
                    size={32} 
                    strokeWidth={2}
                    color={formData.goalCategory === goal.value ? COLORS.orange : COLORS.navy}
                  />
                </div>
                <div className="text-sm font-medium">{goal.value}</div>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => handleGoalCategorySelect('custom')}
            className="p-4 border-2 rounded-lg text-left transition-all"
            style={{
              borderColor: formData.goalCategory === 'custom' ? COLORS.orange : '#e5e7eb',
              backgroundColor: formData.goalCategory === 'custom' ? `${COLORS.orange}10` : 'white'
            }}
          >
            <div className="mb-2">
              <Target 
                size={32} 
                strokeWidth={2}
                color={formData.goalCategory === 'custom' ? COLORS.orange : COLORS.navy}
              />
            </div>
            <div className="text-sm font-medium">Custom Goal</div>
          </button>
        </div>
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          Give your goal a name
        </label>
        <input
          name="goalName"
          type="text"
          required
          value={formData.goalName}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
          placeholder="e.g., Beach House in Byron Bay"
          disabled={formData.goalCategory && formData.goalCategory !== 'custom'}
        />
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          How much wealth are you looking to build?
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
          <input
            name="goalAmount"
            type="number"
            required
            value={formData.goalAmount}
            onChange={handleInputChange}
            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
            placeholder="500,000"
          />
        </div>
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          When do you want to reach this goal?
        </label>
        <select
          name="goalTimeHorizon"
          required
          value={formData.goalTimeHorizon}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
        >
          <option value="">Select timeframe</option>
          <option value="2">2 years</option>
          <option value="3">3 years</option>
          <option value="5">5 years</option>
          <option value="7">7 years</option>
          <option value="10">10 years</option>
          <option value="15">15 years</option>
          <option value="20+">20+ years</option>
        </select>
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          Upload an image for your goal (optional)
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#FF8C42] transition-colors cursor-pointer">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileUpload('goalImage', e.target.files[0])}
            className="hidden"
            id="goalImage"
          />
          <label htmlFor="goalImage" className="cursor-pointer">
            {formData.goalImage ? (
              <div className="flex items-center justify-center gap-2 text-green-600">
                <Check size={20} />
                <span>{formData.goalImage.name}</span>
              </div>
            ) : (
              <div>
                <Upload className="mx-auto mb-2" size={32} style={{ color: COLORS.orange }} />
                <p className="text-sm text-gray-600">Add a picture to inspire you</p>
              </div>
            )}
          </label>
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#FF8C42] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Briefcase size={32} strokeWidth={2} color={COLORS.orange} />
        </div>
        <h3 className="text-2xl font-light mb-2" style={{ fontFamily: 'Georgia, serif', color: COLORS.navy }}>
          Link Your Bank Account
        </h3>
        <p className="text-gray-600">
          Connect your bank to start funding your {formData.goalName || 'goal'}.
        </p>
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          Bank Name
        </label>
        <select
          name="bankName"
          required
          value={formData.bankName}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
        >
          <option value="">Select your bank</option>
          <option value="CBA">Commonwealth Bank</option>
          <option value="NAB">NAB</option>
          <option value="ANZ">ANZ</option>
          <option value="Westpac">Westpac</option>
          <option value="ING">ING</option>
          <option value="Macquarie">Macquarie</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          BSB
        </label>
        <input
          name="bsb"
          type="text"
          required
          value={formData.bsb}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
          placeholder="XXX-XXX"
          maxLength="7"
        />
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          Account Number
        </label>
        <input
          name="accountNumber"
          type="text"
          required
          value={formData.accountNumber}
          onChange={handleInputChange}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42]"
          placeholder="XXXXXXXXX"
          maxLength="10"
        />
      </div>
    </div>
  );

  const renderStep7 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-[#FF8C42] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
          <DollarSign size={32} strokeWidth={2} color={COLORS.orange} />
        </div>
        <h3 className="text-2xl font-light mb-2" style={{ fontFamily: 'Georgia, serif', color: COLORS.navy }}>
          Make Your First Deposit
        </h3>
        <p className="text-gray-600">
          Fund your {formData.goalName || 'goal'} and start building wealth.
        </p>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg mb-6">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-500 mb-1">Goal</div>
            <div className="font-medium" style={{ color: COLORS.navy }}>{formData.goalName}</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Target Amount</div>
            <div className="font-medium" style={{ color: COLORS.navy }}>${parseInt(formData.goalAmount || 0).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Time Horizon</div>
            <div className="font-medium" style={{ color: COLORS.navy }}>{formData.goalTimeHorizon} years</div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Bank</div>
            <div className="font-medium" style={{ color: COLORS.navy }}>{formData.bankName}</div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-base font-medium mb-2" style={{ color: COLORS.navy }}>
          Initial Deposit Amount (Minimum $1,000)
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
          <input
            name="initialDeposit"
            type="number"
            required
            min="1000"
            value={formData.initialDeposit}
            onChange={handleInputChange}
            className="w-full pl-8 pr-4 py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF8C42] text-lg"
            placeholder="5,000"
          />
        </div>
        <p className="text-sm text-gray-500 mt-2">
          This will be your first contribution toward your goal.
        </p>
      </div>
    </div>
  );

  const renderSteps = {
    1: renderStep1,
    2: renderStep2,
    3: renderStep3,
    4: renderStep4,
    5: renderStep5,
    6: renderStep6,
    7: renderStep7
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.beige, paddingTop: '80px' }}>
      <div className="max-w-4xl mx-auto px-6 py-12">
        
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-light" style={{ fontFamily: 'Georgia, serif', color: COLORS.navy }}>
              {stepTitles[currentStep - 1]}
            </h2>
            <span className="text-sm text-gray-500">
              Step {currentStep} of {totalSteps}
            </span>
          </div>
          <div style={{ 
            width: '100%', 
            height: '8px', 
            backgroundColor: '#e0e0e0', 
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(currentStep / totalSteps) * 100}%`,
              height: '100%',
              backgroundColor: COLORS.orange,
              transition: 'width 0.3s ease',
              borderRadius: '4px'
            }} />
          </div>
        </div>

        {/* Step Content */}
        <Card className="p-8 border-0 shadow-lg" style={{ backgroundColor: COLORS.white }}>
          {renderSteps[currentStep]()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <Button
              onClick={handleBack}
              disabled={currentStep === 1}
              variant="outline"
              className="px-6"
              style={{ 
                borderColor: COLORS.navy,
                color: COLORS.navy
              }}
            >
              <ChevronLeft size={20} className="mr-2" />
              Back
            </Button>
            
            <Button
              onClick={handleNext}
              className="px-8"
              style={{ backgroundColor: COLORS.orange }}
            >
              {currentStep === totalSteps ? 'Complete Setup' : 'Continue'}
              {currentStep < totalSteps && <ChevronRight size={20} className="ml-2" />}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingNew;
